/**
 * Native OAuth via the system browser + deep-link callback.
 *
 * Google blocks OAuth inside embedded WebViews ("disallowed_useragent"), so on
 * native the "Continue with Google" flow opens the system browser (@capacitor/
 * browser). The server callback redirects to `com.buzzbitx.app://auth/callback?
 * code=…` (see server routes/auth/googleOAuth.ts), the OS hands that deep link
 * to the app, and `initNativeDeepLinks` finalises the session.
 *
 * The code redeems only with the PKCE verifier this app made as the button was
 * pressed (lib/googleSignIn), so another app that registers the same URL
 * scheme and catches the deep link holds a code it cannot use.
 *
 * All functions are no-ops on web (Capacitor.isNativePlatform() === false).
 */
import { Capacitor } from '@capacitor/core';
import { setAuthToken, setWorkspaceId, apiRequest } from '../services/api/core';
import { completeGoogleSignIn, forgetGoogleSignIn, googleSignInStartUrl } from './googleSignIn';
import { logger } from './logger';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.buzzbitx.com';

/** Open Google OAuth in the system browser (native only). */
export async function startNativeGoogleLogin(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url: await googleSignInStartUrl('mobile') });
  } catch (err) {
    logger.debug('[nativeAuth] failed to open OAuth browser', err);
  }
}

interface AppleLoginResult {
  ok: boolean;
  error?: string;
}

/**
 * Sign in with Apple (native iOS only). Returns ok=true once our session token
 * is set; the caller navigates into the app. Required by App Store guideline 4.8.
 */
export async function startNativeAppleLogin(): Promise<AppleLoginResult> {
  if (Capacitor.getPlatform() !== 'ios') return { ok: false, error: 'unsupported' };
  try {
    const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
    const result = await SignInWithApple.authorize({
      clientId: 'com.buzzbitx.app',
      redirectURI: `${API_URL}/api/auth/apple`,
      scopes: 'name email',
    });
    const identityToken = result?.response?.identityToken;
    if (!identityToken) return { ok: false, error: 'no_token' };

    const res = await apiRequest<{ success: boolean; data: { token: string; workspaceId?: string } }>(
      '/auth/apple',
      { method: 'POST', body: JSON.stringify({ identityToken }) },
    );
    const token = res?.data?.token;
    if (!token) return { ok: false, error: 'no_session' };

    setAuthToken(token);
    if (res.data.workspaceId) setWorkspaceId(res.data.workspaceId);
    return { ok: true };
  } catch (err) {
    logger.debug('[nativeAuth] apple sign-in failed', err);
    return { ok: false, error: err instanceof Error ? err.message : 'apple_failed' };
  }
}

let deepLinksReady = false;

/** Register the OAuth deep-link handler. Call once at boot (native only). */
export async function initNativeDeepLinks(): Promise<void> {
  if (deepLinksReady || !Capacitor.isNativePlatform()) return;
  deepLinksReady = true;

  try {
    const { App } = await import('@capacitor/app');
    await App.addListener('appUrlOpen', ({ url }) => {
      void handleAuthDeepLink(url);
    });
  } catch (err) {
    logger.debug('[nativeAuth] deep-link listener registration failed', err);
    deepLinksReady = false;
  }
}

async function handleAuthDeepLink(url: string): Promise<void> {
  if (!url || !url.includes('auth/callback')) return;

  // Close the system browser regardless of outcome.
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.close();
  } catch {
    /* browser may already be closed */
  }

  let params: URLSearchParams | null = null;
  try {
    params = new URL(url).searchParams;
  } catch {
    params = null;
  }
  const error = params ? params.get('error') : 'bad_callback';
  if (error || !params) {
    forgetGoogleSignIn();
    window.location.href = `/login?error=${encodeURIComponent(error || 'oauth_failed')}`;
    return;
  }

  const session = await completeGoogleSignIn(params);
  if (!session) {
    window.location.href = `/login?error=${encodeURIComponent('invalid_state')}`;
    return;
  }

  setAuthToken(session.token);
  if (session.workspaceId) setWorkspaceId(session.workspaceId);
  // Full reload so the app boots into the authed shell with the persisted token.
  window.location.href = '/business';
}
